import { supabase } from './supabase';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');

    // 새 버전 감지: installing → installed 상태 변화 감지
    reg.addEventListener('updatefound', () => {
      const newWorker = reg.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // 새 버전 준비됨 — 전역에 저장 후 이벤트 발송
          window.__pendingSW = newWorker;
          window.dispatchEvent(new Event('swUpdateReady'));
        }
      });
    });

    // skipWaiting 완료 후 모든 탭 새로고침
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    return reg;
  } catch {
    return null;
  }
}

export async function getCurrentSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// role: 'gym_owner' | 'member' | 'trainer'
export async function requestAndSubscribe(gymId, role = 'gym_owner') {
  if (!VAPID_PUBLIC_KEY) {
    return { error: 'VAPID 키가 설정되지 않았습니다. 관리자에게 문의하세요.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { error: 'denied' };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    const subJson = subscription.toJSON();
    const p_endpoint = subscription.endpoint;
    const p_auth = subJson.keys?.auth || '';
    const p_p256dh = subJson.keys?.p256dh || '';

    let error;
    if (role === 'member') {
      ({ error } = await supabase.rpc('save_member_push_subscription', { p_endpoint, p_auth, p_p256dh }));
    } else if (role === 'trainer') {
      ({ error } = await supabase.rpc('save_trainer_push_subscription', { p_endpoint, p_auth, p_p256dh }));
    } else {
      ({ error } = await supabase.rpc('save_push_subscription', { p_gym_id: gymId, p_endpoint, p_auth, p_p256dh }));
    }

    return { error };
  } catch (e) {
    return { error: e.message };
  }
}

export async function unsubscribeFromPush(gymId) {
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await supabase.rpc('delete_push_subscription', {
      p_gym_id: gymId,
      p_endpoint: endpoint,
    });
  } catch {
    // ignore
  }
}
